import { Timestamp, collection, doc, getDoc, setDoc, updateDoc, query, where, orderBy, limit, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Notification types for the loyalty points aggregator
 */
export type NotificationType = 
  | 'points_expiry'      // Points about to expire
  | 'deal_alert'          // New deals available
  | 'transaction_added'   // New transaction recorded
  | 'transfer_opportunity' // Good transfer opportunity
  | 'milestone_reached'   // Points milestone reached
  | 'price_drop'          // Flight/hotel price drop
  | 'card_benefit'        // Card benefit reminder
  | 'sync_success'        // Data synced successfully
  | 'sync_failed'         // Sync failed
  | 'low_balance'         // Points balance low
  | 'redemption_suggestion' // AI redemption suggestion
  | 'bonus_opportunity';   // Bonus points opportunity

export type NotificationPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface Notification {
  id: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  actionLabel?: string;
  metadata?: {
    programId?: string;
    programName?: string;
    points?: number;
    value?: number;
    expiryDate?: Date;
    [key: string]: any;
  };
  dismissible: boolean;
  persistent?: boolean; // Whether to keep in notification center
}

/**
 * Notification preferences stored in user settings
 */
export interface NotificationPreferences {
  email: boolean;
  sms: boolean;
  push: boolean;
  deals: boolean;
  expiry: boolean;
  transfers: boolean;
  transactions: boolean;
  milestones: boolean;
  priceDrops: boolean;
  emailFrequency?: 'immediate' | 'daily' | 'weekly' | 'monthly' | 'never';
}

/**
 * Professional notification service for managing user notifications
 * Uses Firestore for persistent storage with localStorage cache
 */
class NotificationService {
  private static readonly STORAGE_KEY = 'user_notifications_';
  private static readonly MAX_NOTIFICATIONS = 100; // Keep last 100 notifications
  private static readonly COLLECTION_NAME = 'notifications';
  private listeners: Set<(notifications: Notification[]) => void> = new Set();
  private useFirestore: boolean = true; // Toggle for Firestore usage

  /**
   * Get user-specific storage key
   */
  private getUserKey(userId: string): string {
    return `${NotificationService.STORAGE_KEY}${userId}`;
  }

  /**
   * Generate unique notification ID
   */
  private generateId(): string {
    return `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get all notifications for a user (from Firestore with localStorage cache)
   */
  async getNotifications(userId: string): Promise<Notification[]> {
    if (!userId) return [];
    
    try {
      // Try Firestore first
      if (this.useFirestore && db) {
        const notifications = await this.getNotificationsFromFirestore(userId);
        if (notifications.length > 0) {
          // Update localStorage cache
          this.cacheNotificationsLocally(userId, notifications);
          return notifications;
        }
      }
      
      // Fallback to localStorage
      return this.getNotificationsFromLocalStorage(userId);
    } catch (error) {
      console.error('Error loading notifications from Firestore, using localStorage:', error);
      return this.getNotificationsFromLocalStorage(userId);
    }
  }

  /**
   * Get notifications from Firestore
   */
  private async getNotificationsFromFirestore(userId: string): Promise<Notification[]> {
    try {
      const notificationsRef = collection(db, NotificationService.COLLECTION_NAME);
      const q = query(
        notificationsRef,
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(NotificationService.MAX_NOTIFICATIONS)
      );
      
      const querySnapshot = await getDocs(q);
      const notifications: Notification[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        notifications.push({
          id: doc.id,
          type: data.type,
          priority: data.priority,
          title: data.title,
          message: data.message,
          timestamp: data.timestamp?.toDate() || new Date(),
          read: data.read || false,
          actionUrl: data.actionUrl,
          actionLabel: data.actionLabel,
          metadata: data.metadata,
          dismissible: data.dismissible !== false,
          persistent: data.persistent,
        });
      });
      
      console.log(`📦 Loaded ${notifications.length} notifications from Firestore for user:`, userId);
      return notifications;
    } catch (error) {
      console.error('Error fetching from Firestore:', error);
      throw error;
    }
  }

  /**
   * Get notifications from localStorage (cache/fallback)
   */
  private getNotificationsFromLocalStorage(userId: string): Notification[] {
    try {
      const key = this.getUserKey(userId);
      const stored = localStorage.getItem(key);
      if (!stored) return [];
      
      const notifications = JSON.parse(stored);
      return notifications.map((n: any) => ({
        ...n,
        timestamp: new Date(n.timestamp),
        metadata: n.metadata ? {
          ...n.metadata,
          expiryDate: n.metadata.expiryDate ? new Date(n.metadata.expiryDate) : undefined
        } : undefined
      }));
    } catch (error) {
      console.error('Error loading notifications from localStorage:', error);
      return [];
    }
  }

  /**
   * Cache notifications in localStorage
   */
  private cacheNotificationsLocally(userId: string, notifications: Notification[]): void {
    try {
      const key = this.getUserKey(userId);
      localStorage.setItem(key, JSON.stringify(notifications));
    } catch (error) {
      console.error('Error caching notifications:', error);
    }
  }

  /**
   * Migrate notifications from localStorage to Firestore
   */
  async migrateLocalStorageToFirestore(userId: string): Promise<void> {
    if (!this.useFirestore || !db) return;
    
    try {
      console.log('[MIGRATION] Starting localStorage to Firestore migration for user:', userId);
      
      // Get all localStorage notifications
      const localNotifications = this.getNotificationsFromLocalStorage(userId);
      if (localNotifications.length === 0) {
        console.log('[MIGRATION] No localStorage notifications to migrate');
        return;
      }
      
      console.log('[MIGRATION] Found', localNotifications.length, 'notifications to migrate');
      
      // Check if Firestore already has notifications
      const firestoreNotifications = await this.getNotificationsFromFirestore(userId);
      if (firestoreNotifications.length > 0) {
        console.log('[MIGRATION] Firestore already has', firestoreNotifications.length, 'notifications, skipping migration');
        return;
      }
      
      // Migrate each notification to Firestore
      const migrationPromises = localNotifications.map(notification => {
        const notificationRef = doc(db, NotificationService.COLLECTION_NAME, notification.id);
        // Filter out undefined fields to avoid Firestore errors
        const firestoreData: any = {
          userId,
          type: notification.type,
          priority: notification.priority,
          title: notification.title,
          message: notification.message,
          timestamp: Timestamp.fromDate(notification.timestamp),
          read: notification.read || false,
          dismissible: notification.dismissible !== false,
        };
        // Only add optional fields if they have values
        if (notification.actionUrl) firestoreData.actionUrl = notification.actionUrl;
        if (notification.actionLabel) firestoreData.actionLabel = notification.actionLabel;
        if (notification.metadata) firestoreData.metadata = notification.metadata;
        if (notification.persistent) firestoreData.persistent = notification.persistent;
        
        return setDoc(notificationRef, firestoreData);
      });
      
      await Promise.all(migrationPromises);
      console.log('[MIGRATION] ✅ Successfully migrated', localNotifications.length, 'notifications to Firestore');
    } catch (error) {
      console.error('[MIGRATION] ❌ Error during migration:', error);
      // Don't throw - migration failure shouldn't break the app
    }
  }

  /**
   * Save notifications to both Firestore and localStorage
   */
  private async saveNotifications(userId: string, notifications: Notification[]): Promise<void> {
    try {
      const trimmed = notifications.slice(0, NotificationService.MAX_NOTIFICATIONS);
      
      // Save to localStorage immediately (for fast access)
      this.cacheNotificationsLocally(userId, trimmed);
      
      // Save to Firestore (for persistence)
      if (this.useFirestore && db) {
        // We don't save the entire array, Firestore handles individual docs
        // This is just to trigger listeners
      }
      
      // Notify listeners
      this.notifyListeners(trimmed);
    } catch (error) {
      console.error('Error saving notifications:', error);
    }
  }

  /**
   * Add a new notification (saves to Firestore + localStorage)
   */
  async addNotification(
    userId: string,
    notification: Omit<Notification, 'id' | 'timestamp' | 'read'>
  ): Promise<Notification> {
    const notificationId = this.generateId();
    const newNotification: Notification = {
      ...notification,
      id: notificationId,
      timestamp: new Date(),
      read: false,
    };

    try {
      // Save to Firestore
      if (this.useFirestore && db) {
        try {
          console.log('[SERVICE] addNotification: Saving to Firestore with userId:', userId);
          const notificationRef = doc(db, NotificationService.COLLECTION_NAME, notificationId);
          // Filter out undefined fields to avoid Firestore errors
          const firestoreData: any = {
            userId,
            type: newNotification.type,
            priority: newNotification.priority,
            title: newNotification.title,
            message: newNotification.message,
            timestamp: Timestamp.fromDate(newNotification.timestamp),
            read: newNotification.read,
            dismissible: newNotification.dismissible,
            persistent: newNotification.persistent,
          };
          // Only add optional fields if they have values
          if (newNotification.actionUrl) firestoreData.actionUrl = newNotification.actionUrl;
          if (newNotification.actionLabel) firestoreData.actionLabel = newNotification.actionLabel;
          if (newNotification.metadata) firestoreData.metadata = newNotification.metadata;
          
          await setDoc(notificationRef, firestoreData);
          console.log('📬 Notification saved to Firestore:', notificationId);
        } catch (firestoreError) {
          console.error('[SERVICE] addNotification: ❌ Firestore write failed:', firestoreError);
          // Continue with localStorage only
        }
      }

      // Update localStorage cache
      const existing = this.getNotificationsFromLocalStorage(userId);
      const updated = [newNotification, ...existing];
      this.cacheNotificationsLocally(userId, updated.slice(0, NotificationService.MAX_NOTIFICATIONS));
      
      // Notify listeners
      this.notifyListeners(updated);

      console.log('📬 Notification added:', newNotification.type, newNotification.title);
      return newNotification;
    } catch (error) {
      console.error('Error adding notification:', error);
      // Fallback to localStorage only
      const existing = this.getNotificationsFromLocalStorage(userId);
      const updated = [newNotification, ...existing];
      this.cacheNotificationsLocally(userId, updated.slice(0, NotificationService.MAX_NOTIFICATIONS));
      this.notifyListeners(updated);
      return newNotification;
    }
  }

  /**
   * Mark notification as read (updates Firestore + localStorage)
   */
  async markAsRead(userId: string, notificationId: string): Promise<void> {
    try {
      // Update Firestore
      if (this.useFirestore && db) {
        const notificationRef = doc(db, NotificationService.COLLECTION_NAME, notificationId);
        await updateDoc(notificationRef, { read: true });
        console.log('✓ Notification marked as read in Firestore:', notificationId);
      }

      // Re-fetch fresh notifications from Firestore
      const updatedNotifications = await this.getNotifications(userId);
      console.log('🔄 Fetched updated notifications after mark as read');
      this.notifyListeners(updatedNotifications);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error; // Propagate error to UI
    }
  }

  /**
   * Mark all notifications as read (updates Firestore + localStorage)
   */
  async markAllAsRead(userId: string): Promise<void> {
    try {
      console.log('[SERVICE] markAllAsRead: Starting for user:', userId);
      const notifications = await this.getNotifications(userId);
      console.log('[SERVICE] markAllAsRead: Got notifications:', notifications.length);
      const unreadNotifications = notifications.filter(n => !n.read);
      console.log('[SERVICE] markAllAsRead: Unread notifications:', unreadNotifications.length);

      // Update Firestore
      if (this.useFirestore && db) {
        console.log('[SERVICE] markAllAsRead: Updating Firestore...');
        const updatePromises = unreadNotifications.map(n => {
          const notificationRef = doc(db, NotificationService.COLLECTION_NAME, n.id);
          return updateDoc(notificationRef, { read: true });
        });
        await Promise.all(updatePromises);
        console.log(`[SERVICE] markAllAsRead: ✓ Marked ${unreadNotifications.length} notifications as read in Firestore`);
      }

      // Re-fetch fresh notifications from Firestore
      console.log('[SERVICE] markAllAsRead: Re-fetching from Firestore...');
      const updatedNotifications = await this.getNotifications(userId);
      console.log('[SERVICE] markAllAsRead: Fetched updated notifications:', updatedNotifications.length);
      console.log('[SERVICE] markAllAsRead: About to notify listeners');
      this.notifyListeners(updatedNotifications);
      console.log('[SERVICE] markAllAsRead: ✓ Notified listeners');
    } catch (error) {
      console.error('[SERVICE] markAllAsRead: Error -', error);
      throw error; // Propagate error to UI
    }
  }

  /**
   * Delete a notification (from Firestore + localStorage)
   */
  async deleteNotification(userId: string, notificationId: string): Promise<void> {
    try {
      console.log('[SERVICE] deleteNotification: Starting for notification:', notificationId);
      // Delete from Firestore
      if (this.useFirestore && db) {
        try {
          const notificationRef = doc(db, NotificationService.COLLECTION_NAME, notificationId);
          await deleteDoc(notificationRef);
          console.log('🗑️ Notification deleted from Firestore:', notificationId);
        } catch (firestoreError) {
          console.log('[SERVICE] deleteNotification: Firestore delete failed (may not exist):', firestoreError);
          // Continue anyway - delete from localStorage
        }
      }

      // Re-fetch fresh notifications from Firestore (or localStorage fallback)
      const updatedNotifications = await this.getNotifications(userId);
      console.log('🔄 Fetched updated notifications after delete:', updatedNotifications.length);
      this.notifyListeners(updatedNotifications);
    } catch (error) {
      console.error('[SERVICE] deleteNotification: Error -', error);
      throw error; // Propagate error to UI
    }
  }

  /**
   * Clear all notifications (from Firestore + localStorage)
   */
  async clearAll(userId: string): Promise<void> {
    try {
      console.log('[SERVICE] clearAll: Starting for user:', userId);
      // Delete from Firestore
      if (this.useFirestore && db) {
        try {
          console.log('[SERVICE] clearAll: Getting notifications from Firestore...');
          const notifications = await this.getNotifications(userId);
          console.log('[SERVICE] clearAll: Got', notifications.length, 'notifications to delete');
          const deletePromises = notifications.map(n => {
            const notificationRef = doc(db, NotificationService.COLLECTION_NAME, n.id);
            return deleteDoc(notificationRef);
          });
          await Promise.all(deletePromises);
          console.log(`[SERVICE] clearAll: ✓ Cleared ${notifications.length} notifications from Firestore`);
        } catch (firestoreError) {
          console.log('[SERVICE] clearAll: Firestore delete failed (permissions or docs dont exist):', firestoreError);
          // Continue anyway - clear localStorage
        }
      }

      // Clear localStorage
      console.log('[SERVICE] clearAll: Clearing localStorage...');
      const key = this.getUserKey(userId);
      localStorage.removeItem(key);
      
      // Notify listeners with empty array
      console.log('[SERVICE] clearAll: Notifying listeners with empty array');
      this.notifyListeners([]);
      console.log('[SERVICE] clearAll: ✓ Completed');
    } catch (error) {
      console.error('[SERVICE] clearAll: Error -', error);
      throw error; // Propagate error to UI
    }
  }

  /**
   * Get unread notification count - only count active (not dismissed) unread notifications
   */
  async getUnreadCount(userId: string): Promise<number> {
    const notifications = await this.getNotifications(userId);
    const unreadCount = notifications.filter(n => !n.read && n.dismissible !== false).length;
    console.log('🔔 getUnreadCount:', { total: notifications.length, unread: unreadCount, notifications: notifications.slice(0, 3).map(n => ({ title: n.title, read: n.read, dismissible: n.dismissible })) });
    return unreadCount;
  }

  /**
   * Subscribe to notification changes
   */
  subscribe(callback: (notifications: Notification[]) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of notification changes
   */
  private notifyListeners(notifications: Notification[]): void {
    this.listeners.forEach(listener => listener(notifications));
  }

  /**
   * Check for points expiry and create notifications
   */
  async checkPointsExpiry(userId: string, programs: any[], daysThreshold: number = 60): Promise<void> {
    const now = new Date();
    const thresholdDate = new Date();
    thresholdDate.setDate(thresholdDate.getDate() + daysThreshold);

    const recentNotifications = await this.getNotifications(userId);

    for (const program of programs) {
      if (program.expiryDate) {
        const expiryDate = new Date(program.expiryDate);
        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        // Only notify if within threshold and not already notified recently
        if (daysUntilExpiry > 0 && daysUntilExpiry <= daysThreshold) {
          const alreadyNotified = recentNotifications.some(n => 
            n.type === 'points_expiry' &&
            n.metadata?.programId === program.id &&
            (now.getTime() - n.timestamp.getTime()) < 7 * 24 * 60 * 60 * 1000 // Within last 7 days
          );

          if (!alreadyNotified) {
            let priority: NotificationPriority = 'low';
            if (daysUntilExpiry <= 7) priority = 'urgent';
            else if (daysUntilExpiry <= 30) priority = 'high';
            else if (daysUntilExpiry <= 45) priority = 'medium';

            await this.addNotification(userId, {
              type: 'points_expiry',
              priority,
              title: `Points Expiring Soon!`,
              message: `${program.points.toLocaleString()} ${program.name} points expire in ${daysUntilExpiry} days`,
              actionUrl: '/dashboard',
              actionLabel: 'View Details',
              metadata: {
                programId: program.id,
                programName: program.name,
                points: program.points,
                expiryDate: expiryDate,
              },
              dismissible: true,
              persistent: true,
            });
          }
        }
      }
    }
  }

  /**
   * Notify about new transaction
   */
  notifyTransaction(
    userId: string, 
    programName: string, 
    points: number, 
    type: 'earned' | 'redeemed'
  ): void {
    this.addNotification(userId, {
      type: 'transaction_added',
      priority: 'low',
      title: `Points ${type === 'earned' ? 'Earned' : 'Redeemed'}`,
      message: `${Math.abs(points).toLocaleString()} points ${type === 'earned' ? 'added to' : 'redeemed from'} ${programName}`,
      actionUrl: '/cards',
      actionLabel: 'View Details',
      metadata: {
        programName,
        points,
      },
      dismissible: true,
    });
  }

  /**
   * Notify about milestone reached
   */
  notifyMilestone(
    userId: string,
    programName: string,
    milestone: number,
    currentPoints: number
  ): void {
    this.addNotification(userId, {
      type: 'milestone_reached',
      priority: 'medium',
      title: '🎉 Milestone Reached!',
      message: `You've reached ${milestone.toLocaleString()} points in ${programName}! Current balance: ${currentPoints.toLocaleString()}`,
      actionUrl: '/cards',
      actionLabel: 'View Program',
      metadata: {
        programName,
        milestone,
        points: currentPoints,
      },
      dismissible: true,
      persistent: true,
    });
  }

  /**
   * Notify about deal/promotion
   */
  notifyDeal(
    userId: string,
    title: string,
    description: string,
    actionUrl?: string
  ): void {
    this.addNotification(userId, {
      type: 'deal_alert',
      priority: 'medium',
      title: `🔥 ${title}`,
      message: description,
      actionUrl: actionUrl || '/deals',
      actionLabel: 'View Deal',
      dismissible: true,
      persistent: true,
    });
  }

  /**
   * Notify about transfer opportunity
   */
  notifyTransferOpportunity(
    userId: string,
    fromProgram: string,
    toProgram: string,
    bonusPercentage: number
  ): void {
    this.addNotification(userId, {
      type: 'transfer_opportunity',
      priority: 'high',
      title: '⚡ Transfer Bonus Available!',
      message: `Transfer from ${fromProgram} to ${toProgram} and get ${bonusPercentage}% bonus points`,
      actionUrl: '/airlines',
      actionLabel: 'View Programs',
      metadata: {
        fromProgram,
        toProgram,
        bonusPercentage,
      },
      dismissible: true,
      persistent: true,
    });
  }

  /**
   * Notify about price drop
   */
  notifyPriceDrop(
    userId: string,
    route: string,
    oldPrice: number,
    newPrice: number,
    percentageDrop: number
  ): void {
    this.addNotification(userId, {
      type: 'price_drop',
      priority: 'high',
      title: '✈️ Price Drop Alert!',
      message: `${route} dropped ${percentageDrop}% to $${newPrice.toFixed(2)}`,
      actionUrl: '/deals',
      actionLabel: 'Book Now',
      metadata: {
        route,
        oldPrice,
        newPrice,
        percentageDrop,
      },
      dismissible: true,
      persistent: true,
    });
  }

  /**
   * Notify about low balance
   */
  notifyLowBalance(
    userId: string,
    programName: string,
    currentPoints: number,
    threshold: number
  ): void {
    this.addNotification(userId, {
      type: 'low_balance',
      priority: 'low',
      title: 'Low Points Balance',
      message: `Your ${programName} balance (${currentPoints.toLocaleString()}) is below ${threshold.toLocaleString()} points`,
      actionUrl: '/cards',
      actionLabel: 'View Program',
      metadata: {
        programName,
        points: currentPoints,
        threshold,
      },
      dismissible: true,
    });
  }

  /**
   * Notify sync status
   */
  notifySyncStatus(userId: string, success: boolean, message: string): void {
    this.addNotification(userId, {
      type: success ? 'sync_success' : 'sync_failed',
      priority: success ? 'low' : 'medium',
      title: success ? 'Sync Complete' : 'Sync Failed',
      message,
      dismissible: true,
    });
  }

  /**
   * Check user preferences and return if notification type is enabled
   */
  shouldNotify(preferences: NotificationPreferences, type: NotificationType): boolean {
    switch (type) {
      case 'points_expiry':
        return preferences.expiry;
      case 'deal_alert':
      case 'bonus_opportunity':
        return preferences.deals;
      case 'transaction_added':
        return preferences.transactions || true; // Default to true
      case 'transfer_opportunity':
        return preferences.transfers;
      case 'price_drop':
        return preferences.priceDrops || true;
      case 'milestone_reached':
        return preferences.milestones || true;
      default:
        return true;
    }
  }
}

export const notificationService = new NotificationService();
export default notificationService;
